/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Staff } from '@wsphere/staff/domain';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { RoleBundlesService } from '../../services';

const VISIBLE_ROLE_CHIPS = 4;

function countLabel(count: number) {
  return `${count} role${count === 1 ? '' : 's'}`;
}

/**
 * Multi-select of role bundles, with the picked ones shown below as cards.
 * Not `vs-select`: it stamps options through `innerHTML`, which strips directives, so the hover tooltip
 * listing a bundle's roles is impossible inside it.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-bundle-picker',
  templateUrl: './bundle-picker.component.html',
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: BundlePickerComponent }],
})
export class BundlePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  constructor(
    public elementRef: ElementRef,
    private bundleSvc: RoleBundlesService
  ) {}

  @HostBinding('class') _class = 'block';

  @Input() disabled = false;
  @Input() placeholder = 'Add a bundle';

  @PropertyListener('value') value$ = new BehaviorSubject<string[]>([]);
  @Input() value: string[] = [];

  /**
   * Role keys a member actually holds from each bundle, keyed by bundleId. Given, the cards show these
   * instead of the bundle's current definition - a bundle edited but not yet applied has not changed what
   * anyone holds, so showing its new roles on their card would misstate their access.
   * Omit when there is nothing granted yet (an invite), where the definition is what will be granted.
   */
  @PropertyListener('grantedRoleKeys') grantedRoleKeys$ = new BehaviorSubject<Record<string, string[]> | null>(null);
  @Input() grantedRoleKeys: Record<string, string[]> | null = null;

  @Output() bundleAdded = new EventEmitter<string>();
  @Output() bundleRemoved = new EventEmitter<string>();
  @Output() valueChanges = new EventEmitter<string[]>();

  overlayOpen = false;

  readonly bundles$ = new BehaviorSubject<Staff.Security.RoleBundle[]>([]);
  readonly searchQuery$ = new BehaviorSubject<string>('');

  /**
   * Only active bundles can be handed out. Name-only search: matching role titles too would make "manage"
   * hit every bundle containing a "... Manager" role. In-memory, so no debounce needed.
   */
  readonly filteredBundles$ = combineLatest([this.bundles$, this.searchQuery$]).pipe(
    map(([bundles, search]) => {
      const active = bundles.filter(bundle => bundle.isActive);

      const query = search.trim().toLowerCase();
      if (!query) return active;

      return active.filter(bundle => bundle.name.toLowerCase().includes(query));
    })
  );

  /** Resolved against the full catalog, not the dropdown's list: a bundle with no card cannot be removed. */
  readonly selectedBundles$ = combineLatest([this.bundles$, this.value$]).pipe(
    map(([bundles, value]) =>
      value
        .map(id => bundles.find(bundle => bundle.id === id))
        .filter((bundle): bundle is Staff.Security.RoleBundle => !!bundle)
    )
  );

  readonly selectedNames$ = this.selectedBundles$.pipe(map(bundles => bundles.map(bundle => bundle.name).join(', ')));

  async ngOnInit() {
    try {
      // Deactivated ones included: a bundle deactivated after assignment still needs a card to be removable.
      const { items } = await this.bundleSvc.list({ page: 1, limit: 100 });
      this.bundles$.next(items);
    } catch {
      this.bundles$.next([]);
    }
  }

  roleTitle(roleKey: string) {
    return Staff.Security.getRole(roleKey)?.display.title ?? roleKey;
  }

  roleTheme(roleKey: string): ThemeColor {
    return Staff.Security.getRole(roleKey)?.display.theme ?? 'default';
  }

  /** The bundle's own roles - what it grants today. */
  definedRoleKeys(bundle: Staff.Security.RoleBundle) {
    return (bundle.items ?? []).map(item => item.roleKey);
  }

  /** What the member actually holds from this bundle, falling back to the definition when nothing is granted yet. */
  roleKeysOn(bundle: Staff.Security.RoleBundle) {
    return this.grantedRoleKeys ? (this.grantedRoleKeys[bundle.id] ?? []) : this.definedRoleKeys(bundle);
  }

  /** True when the bundle has been edited since the member got it, so an apply is pending for them. */
  isOutOfDate(bundle: Staff.Security.RoleBundle) {
    if (!this.grantedRoleKeys) return false;

    const held = new Set(this.roleKeysOn(bundle));
    const defined = this.definedRoleKeys(bundle);

    return defined.length !== held.size || defined.some(roleKey => !held.has(roleKey));
  }

  /** For the dropdown: what the bundle grants, not what this member happens to hold. */
  definedCountLabel(bundle: Staff.Security.RoleBundle) {
    return countLabel(this.definedRoleKeys(bundle).length);
  }

  /** For the cards: what the member actually holds from the bundle. */
  roleCountLabel(bundle: Staff.Security.RoleBundle) {
    return countLabel(this.roleKeysOn(bundle).length);
  }

  visibleRoles(bundle: Staff.Security.RoleBundle) {
    return this.roleKeysOn(bundle).slice(0, VISIBLE_ROLE_CHIPS);
  }

  hiddenRolesOf(bundle: Staff.Security.RoleBundle) {
    return this.roleKeysOn(bundle).slice(VISIBLE_ROLE_CHIPS);
  }

  hiddenRoleCount(bundle: Staff.Security.RoleBundle) {
    return Math.max(0, this.roleKeysOn(bundle).length - VISIBLE_ROLE_CHIPS);
  }

  isSelected(bundleId: string) {
    return this.value.includes(bundleId);
  }

  toggleOverlay() {
    if (this.disabled) return;
    this.overlayOpen ? this.close() : (this.overlayOpen = true);
  }

  close() {
    this.overlayOpen = false;
    this.searchQuery$.next('');
  }

  ngOnDestroy() {
    this.value$.complete();
    this.searchQuery$.complete();
    this.bundles$.complete();
  }

  toggle(bundleId: string) {
    if (this.disabled) return;
    this.select(this.isSelected(bundleId) ? this.value.filter(id => id !== bundleId) : [...this.value, bundleId]);
  }

  remove(bundleId: string) {
    if (this.disabled) return;
    this.select(this.value.filter(id => id !== bundleId));
  }

  /** Emits each add/remove so callers that apply changes immediately know what changed. */
  select(selected: string[]) {
    const added = selected.filter(bundleId => !this.value.includes(bundleId));
    const removed = this.value.filter(bundleId => !selected.includes(bundleId));

    this.value = selected;
    this.onTouched();
    this.onChange(selected);
    this.valueChanges.emit(selected);

    added.forEach(bundleId => this.bundleAdded.emit(bundleId));
    removed.forEach(bundleId => this.bundleRemoved.emit(bundleId));
  }

  onChange = (_value: string[]) => {};
  onTouched = () => {};

  writeValue(value: string[]): void {
    this.value = value || [];
  }

  registerOnChange(onChange: (_value: string[]) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }
}
